/**
 * One line of the order, said in a handful of words.
 *
 * The creator used to draw every position as a permanently open form. Five
 * positions measured 3 483 px of list inside a 4 811 px page — 5.3 screens at
 * 1440×900 — so the only way to see what was in the order was to scroll through
 * every form in it. There was no overview at any zoom level.
 *
 * A closed position needs to say enough that nobody has to open it to recognise
 * it, and no more: the level, the count, the date, the place. That is what this
 * builds. It is deliberately NOT a re-render of the fields — a summary that
 * lists everything is the form again, in smaller type.
 *
 * Read off the same capability columns the fields are drawn from, so a position
 * cannot summarise itself as something it does not ask for.
 */

import { bandAt, bandLabel } from '@repo/core/film-pricing'

import {
  bookedMinutes,
  type CreatorCityView,
  type CreatorOfferingView,
  type CreatorPick,
  formatMinutes,
  hasQuantity,
  needsCity,
  needsDate,
  needsLengthBand,
  needsRange,
  needsTime,
  pickDays,
} from '@/lib/creator'
import { formatDay } from '@/lib/dates'
import { returnDay } from '@/lib/rental'
import { pluralize } from '@/lib/tiers'

/**
 * The facts, in the order a person reads them: what level, how much, when,
 * where. Every entry is already formatted for display.
 */
export function lineFacts(
  offering: CreatorOfferingView,
  pick: CreatorPick,
  variantName: string | undefined,
  cities: readonly CreatorCityView[],
): string[] {
  const facts: string[] = []

  if (variantName) facts.push(variantName)

  // A film's length is its quantity — the band is the number being priced, so
  // printing both would be the same fact twice in different units.
  if (needsLengthBand(offering)) {
    const band = pick.lengthBand === null ? null : bandAt(pick.lengthBand)
    if (band) facts.push(bandLabel(band))
  } else if (hasQuantity(offering) && pick.qty > 0) {
    facts.push(
      offering.bookingMode === 'TIME_WINDOW'
        ? formatMinutes(bookedMinutes(offering, pick))
        : `${pick.qty} ${pluralize(pick.qty, offering.unitForms)}`,
    )
  }

  if (needsDate(offering) && pick.from) {
    if (needsRange(offering)) {
      // The window, and NOT the day count beside it: a hire's variant is its
      // term (spec §11 · I4), so the level already read "3 doby" one fact
      // earlier. It was printed twice — and being identical text, the two
      // collided as React keys.
      const back = returnDay(pick.from, pickDays(offering, pick))
      facts.push(back ? `${formatDay(pick.from)} → ${formatDay(back)}` : formatDay(pick.from))
    } else {
      facts.push(
        needsTime(offering, pick) && pick.time
          ? `${formatDay(pick.from)}, ${pick.time}`
          : formatDay(pick.from),
      )
    }
  }

  if (needsCity(offering) && pick.citySlug) {
    const city = cities.find((c) => c.slug === pick.citySlug)
    if (city) facts.push(city.name)
  }

  return facts
}
